import { Locales } from '@griply/types';

import {
  buildDateSeparator,
  buildDayListSeparator,
  getDayMap,
  getDayPattern,
  getMonthMap,
  getMonthPattern,
  getRruleKeywords,
  matchesKeyword,
} from './locale-vocabulary';

export type RruleMatchContext = {
  k: Record<string, string>;
  dayMap: Record<string, string>;
  monthMap: Record<string, number>;
  dayPattern: string;
  monthPattern: string;
  ordinalWordsPattern: string;
  ordinalPattern: string;
  dayListPattern: string;
  dateSeparator: string;
};

type RruleMatcher = {
  pattern: (ctx: RruleMatchContext) => string;
  flags?: string;
  build: (match: RegExpMatchArray, ctx: RruleMatchContext) => string | null;
};

export const buildMatchContext = (language: Locales): RruleMatchContext => {
  const k = getRruleKeywords(language);
  const ordinalPattern = `\\d{1,2}(?:${k.ordinals})?`;
  const dayListSeparator = buildDayListSeparator(k.and);

  return {
    k,
    dayMap: getDayMap(language),
    monthMap: getMonthMap(language),
    dayPattern: getDayPattern(language),
    monthPattern: getMonthPattern(language),
    // eslint-disable-next-line max-len -- regex
    ordinalWordsPattern: `(${k.first}|${k.second}|${k.third}|${k.fourth}|${k.fifth}|${k.last})`,
    ordinalPattern,
    dayListPattern: `${ordinalPattern}(?:${dayListSeparator}${ordinalPattern})*`,
    dateSeparator: buildDateSeparator(k.of),
  };
};

const ORDINAL_VALUES: Record<string, string> = {
  first: '+1',
  second: '+2',
  third: '+3',
  fourth: '+4',
  fifth: '+5',
  last: '-1',
};

const WEEKDAY_BYDAY = 'MO,TU,WE,TH,FR';
const WEEKEND_BYDAY = 'SA,SU';
const WEEKDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const getOrdinalValue = (word: string, k: Record<string, string>) =>
  Object.entries(ORDINAL_VALUES).find(([key]) =>
    matchesKeyword(word, k[key]),
  )?.[1];

const getGroupByDay = (dayWord: string, k: Record<string, string>) => {
  if (matchesKeyword(dayWord, k.weekday)) {
    return WEEKDAY_BYDAY;
  }
  return matchesKeyword(dayWord, k.weekend) ? WEEKEND_BYDAY : null;
};

const getDayCode = (dayWord: string, dayMap: Record<string, string>) => {
  const key = Object.keys(dayMap).find((d) => dayWord.startsWith(d));
  return key ? dayMap[key] : null;
};

const getDayCodeList = (daysStr: string, ctx: RruleMatchContext) =>
  [...daysStr.matchAll(new RegExp(ctx.dayPattern, 'g'))]
    .map((m) => ctx.dayMap[m[0].toLowerCase()])
    .filter(Boolean);

const parseCount = (value?: string) => (value ? parseInt(value, 10) : 1);

const withInterval = (rrule: string, count: number) =>
  count > 1 ? `${rrule};INTERVAL=${count}` : rrule;

const toCommaSeparatedList = (value: string, and?: string) =>
  and ? value.replace(new RegExp(`\\s+${and}\\s+`, 'g'), ',') : value;

const parseMonthDays = (daysStr: string, and?: string) => {
  const parts = toCommaSeparatedList(daysStr, and)
    .split(',')
    .map((d) => d.trim());
  const days = parts
    .map((d) => parseInt(d, 10))
    .filter((d) => !isNaN(d) && d > 0 && d < 32);
  return days.length && days.length === parts.length ? days : null;
};

const monthDayRrule = (
  monthName: string,
  dayStr: string,
  ctx: RruleMatchContext,
) => {
  const monthIndex = ctx.monthMap[monthName.toLowerCase()];
  const day = parseInt(dayStr, 10);
  if (monthIndex === undefined || isNaN(day)) {
    return null;
  }
  // A leap year, so "29 feb" validates.
  if (new Date(2024, monthIndex, day).getMonth() !== monthIndex) {
    return null;
  }
  return `FREQ=YEARLY;BYMONTH=${monthIndex + 1};BYMONTHDAY=${day}`;
};

const ordinalDayRrule = (
  freq: string,
  ordinalWord: string,
  dayWord: string,
  ctx: RruleMatchContext,
) => {
  const ordinalValue = getOrdinalValue(ordinalWord, ctx.k);
  if (!ordinalValue) {
    return null;
  }

  const groupByDay = getGroupByDay(dayWord, ctx.k);
  if (groupByDay) {
    const setPos = ordinalValue.replace('+', '');
    return `${freq};BYDAY=${groupByDay};BYSETPOS=${setPos}`;
  }

  const dayCode = getDayCode(dayWord, ctx.dayMap);
  return dayCode ? `${freq};BYDAY=${ordinalValue}${dayCode}` : null;
};

// Most specific first; a matcher that matches but fails its own validation yields to the next.
const RRULE_MATCHERS: RruleMatcher[] = [
  // "every 2 years on Dec 31."
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(?:${c.k.years}|${c.k.year})\\s+${c.k.on}\\s+(?:(${c.monthPattern})${c.dateSeparator}(${c.ordinalPattern})|(${c.ordinalPattern})${c.dateSeparator}(${c.monthPattern}))$`,
    build: (m, c) => {
      const rrule = monthDayRrule(m[2] || m[5], m[3] || m[4], c);
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every 2 months on the 11th and 12th"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(${c.k.months}|${c.k.month})\\s+${c.k.on}\\s+(?:${c.k.the}\\s*)?(${c.dayListPattern})$`,
    build: (m, c) => {
      const days = parseMonthDays(m[3], c.k.and);
      const rrule = days && `FREQ=MONTHLY;BYMONTHDAY=${days.join(',')}`;
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every 2 months on the second tuesday"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(${c.k.months}|${c.k.month})\\s+${c.k.on}\\s+(?:${c.k.the}\\s+)?${c.ordinalWordsPattern}\\s+(${c.dayPattern})$`,
    flags: 'i',
    build: (m, c) => {
      const rrule = ordinalDayRrule('FREQ=MONTHLY', m[3], m[4], c);
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every 2 weeks on mon, fri"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (\\d+) (${c.k.weeks}|${c.k.week})\\s+${c.k.on}\\s+((?:${c.dayPattern}(?:,\\s*${c.dayPattern})*))$`,
    flags: 'i',
    build: (m, c) => {
      const dayCodes = getDayCodeList(m[3], c);
      if (!dayCodes.length) {
        return null;
      }
      const interval = parseInt(m[1], 10);
      return `FREQ=WEEKLY;INTERVAL=${interval};BYDAY=${dayCodes.join(',')}`;
    },
  },
  // "every year on the first monday in feb"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(?:${c.k.years}|${c.k.year})\\s+${c.k.on}\\s+(?:${c.k.the}\\s*)?${c.ordinalWordsPattern}\\s+(${c.dayPattern}|${c.k.weekday}|${c.k.weekend})\\s+${c.k.in}\\s+(${c.monthPattern})$`,
    build: (m, c) => {
      const monthIndex = c.monthMap[m[4].toLowerCase()];
      if (monthIndex === undefined) {
        return null;
      }
      const freq = `FREQ=YEARLY;BYMONTH=${monthIndex + 1}`;
      const rrule = ordinalDayRrule(freq, m[2], m[3].toLowerCase(), c);
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every year on the first monday"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(?:${c.k.years}|${c.k.year})\\s+${c.k.on}\\s+(?:${c.k.the}\\s*)?${c.ordinalWordsPattern}\\s+(${c.dayPattern}|${c.k.weekday}|${c.k.weekend})$`,
    build: (m, c) => {
      const rrule = ordinalDayRrule('FREQ=YEARLY', m[2], m[3].toLowerCase(), c);
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every month on the first weekday"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(?:${c.k.months}|${c.k.month}) ${c.k.on} (?:${c.k.the}\\s*)?\\s*${c.ordinalWordsPattern}\\s+(${c.k.weekday}|${c.k.weekend})$`,
    build: (m, c) => {
      const rrule = ordinalDayRrule('FREQ=MONTHLY', m[2], m[3], c);
      return rrule && withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every month on the last day"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(\\d+)\\s+)?(?:${c.k.months}|${c.k.month}) ${c.k.on} (?:${c.k.the}\\s*)?\\s*${c.ordinalWordsPattern}\\s+${c.k.day}$`,
    build: (m, c) => {
      const ordinalValue = getOrdinalValue(m[2], c.k);
      if (!ordinalValue) {
        return null;
      }
      const rrule = `FREQ=MONTHLY;BYMONTHDAY=${parseInt(ordinalValue, 10)}`;
      return withInterval(rrule, parseCount(m[1]));
    },
  },
  // "every 3 days" — the units that need a day anchor to today.
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `${c.k.every} (\\d+) (${c.k.days}|${c.k.day}|${c.k.weeks}|${c.k.week}|${c.k.months}|${c.k.month}|${c.k.years}|${c.k.year})$`,
    build: (m, { k }) => {
      const count = parseInt(m[1], 10);
      const unit = m[2];
      const now = new Date();
      if (matchesKeyword(unit, k.day) || matchesKeyword(unit, k.days)) {
        return `FREQ=DAILY;INTERVAL=${count}`;
      }
      if (matchesKeyword(unit, k.week) || matchesKeyword(unit, k.weeks)) {
        const day = WEEKDAY_CODES[now.getDay()];
        return `FREQ=WEEKLY;INTERVAL=${count};BYDAY=${day}`;
      }
      if (matchesKeyword(unit, k.month) || matchesKeyword(unit, k.months)) {
        return `FREQ=MONTHLY;INTERVAL=${count};BYMONTHDAY=${now.getDate()}`;
      }
      if (matchesKeyword(unit, k.year) || matchesKeyword(unit, k.years)) {
        const date = `BYMONTH=${now.getMonth() + 1};BYMONTHDAY=${now.getDate()}`;
        return `FREQ=YEARLY;INTERVAL=${count};${date}`;
      }
      return null;
    },
  },
  {
    pattern: (c) => `^(?:${c.k.daily}|${c.k.every} ${c.k.day})$`,
    build: () => 'FREQ=DAILY',
  },
  {
    pattern: (c) => `^${c.k.every} ${c.k.weekday}$`,
    build: () => `FREQ=WEEKLY;BYDAY=${WEEKDAY_BYDAY}`,
  },
  {
    pattern: (c) => `^(?:${c.k.weekly}|${c.k.every} ${c.k.week})$`,
    build: () => `FREQ=WEEKLY;BYDAY=${WEEKDAY_CODES[new Date().getDay()]}`,
  },
  {
    pattern: (c) => `^(?:${c.k.monthly}|${c.k.every} ${c.k.month})$`,
    build: () => `FREQ=MONTHLY;BYMONTHDAY=${new Date().getDate()}`,
  },
  {
    pattern: (c) => `^(?:${c.k.yearly}|${c.k.every} ${c.k.year})$`,
    build: () => {
      const now = new Date();
      const date = `BYMONTH=${now.getMonth() + 1};BYMONTHDAY=${now.getDate()}`;
      return `FREQ=YEARLY;${date}`;
    },
  },
  // "every Jan 27." / "every 27. Jan"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every} (?:(${c.monthPattern})${c.dateSeparator}(${c.ordinalPattern})|(${c.ordinalPattern})${c.dateSeparator}(${c.monthPattern}))$`,
    build: (m, c) => monthDayRrule(m[1] || m[4], m[2] || m[3], c),
  },
  // "every monday, friday"
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every}\\s+((?:${c.dayPattern}(?:,\\s*${c.dayPattern})*))$`,
    build: (m, c) => {
      const dayCodes = getDayCodeList(m[1], c);
      return dayCodes.length ? `FREQ=WEEKLY;BYDAY=${dayCodes.join(',')}` : null;
    },
  },
  // "every first monday"
  {
    pattern: (c) =>
      `^${c.k.every}\\s+${c.ordinalWordsPattern}\\s+(${c.dayPattern})$`,
    build: (m, c) => {
      const ordinalValue = getOrdinalValue(m[1], c.k);
      const dayCode = getDayCode(m[2], c.dayMap);
      return ordinalValue && dayCode
        ? `FREQ=MONTHLY;BYDAY=${ordinalValue}${dayCode}`
        : null;
    },
  },
  // "every 28." / "every 10., 15."
  {
    pattern: (c) =>
      // eslint-disable-next-line max-len -- regex
      `^${c.k.every}\\s+(${c.dayListPattern})(?!${c.dateSeparator}${c.monthPattern})`,
    build: (m, c) => {
      const days = parseMonthDays(m[1], c.k.and);
      return days ? `FREQ=MONTHLY;BYMONTHDAY=${days.join(',')}` : null;
    },
  },
];

export const matchRrule = (rruleStr: string, ctx: RruleMatchContext) =>
  RRULE_MATCHERS.reduce<string | null>((found, matcher) => {
    if (found) {
      return found;
    }
    const pattern = new RegExp(matcher.pattern(ctx), matcher.flags);
    const match = rruleStr.match(pattern);
    return match ? matcher.build(match, ctx) : null;
  }, null);
